import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { localeAwareFormat } from 'app/lib/date';
import type { Theme } from 'app/shopify';
import { getLoanETA, LoanETA, LoanStatus, type Loan } from 'app/types/loan';
import { addDays, startOfDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { AppState, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { LoanStatusRow } from 'app/components/loan-status-row';
import { getTimelineEnd, getTimelineMilestones } from './period-timeline.utils';

export function PeriodTimeline({ loan, isService }: { loan: Loan; isService: boolean }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('one-loan');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const refresh = () => setNow(new Date());
    const timeout = setTimeout(refresh, addDays(startOfDay(now), 1).getTime() - Date.now() + 100);
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') refresh();
    });
    return () => {
      clearTimeout(timeout);
      subscription.remove();
    };
  }, [now]);

  const isLate = loan.status === LoanStatus.Accepted && getLoanETA(loan) === LoanETA.late;
  const overdueColor = theme.colors['surface.brand.secondary-4.strong'];
  const timelineEnd = getTimelineEnd(loan, isService, now);
  const milestones = getTimelineMilestones(loan.start, timelineEnd.date, now);
  const dates = { start: new Date(loan.start), today: now, end: new Date(timelineEnd.date) };
  const labels = {
    start: t('start'),
    today: t('calendar.today', { ns: 'loans' }),
    end: t(timelineEnd.label),
  };
  const times = {
    start: loan.listing.timeStart,
    today: undefined,
    end: timelineEnd.isReturned ? undefined : loan.listing.timeEnd,
  };

  const statusSummary = () => (
    <LoanStatusRow
      end={loan.end}
      start={loan.start}
      status={loan.status}
      returned={loan.returned}
      listingSubtype={loan.listing.subtype}
      showClock={false}
      presentation="summary"
    />
  );

  return (
    <Column
      backgroundColor="surface.secondary-neutral"
      borderRadius="radius.md"
      padding="spacing.general.x3"
      gap="spacing.general.x3"
    >
      {statusSummary()}
      <Row>
        {milestones.map((milestone, index) => {
          const isToday = milestone === 'today';
          const isReached = startOfDay(dates[milestone]) <= startOfDay(now);
          const markerColor =
            isLate && milestone !== 'start'
              ? overdueColor
              : theme.colors[isReached ? 'text.primary' : 'border.primary'];
          const nextMilestone = milestones[index + 1];
          const lineStyle = (overdue: boolean, reached: boolean) => ({
            borderTopWidth: 2,
            borderStyle: overdue ? ('dashed' as const) : ('solid' as const),
            borderColor: overdue ? overdueColor : theme.colors[reached ? 'text.primary' : 'border.secondary'],
          });
          return (
            <Column
              key={milestone}
              flex={1}
              style={{ minWidth: 0 }}
              accessible
              accessibilityLabel={`${labels[milestone]}, ${localeAwareFormat(dates[milestone], t('dateFormat_MMMdYYYY', { ns: 'common' }))}${times[milestone] ? `, ${times[milestone]}` : ''}`}
            >
              <View
                accessible={false}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                style={{ height: 28, justifyContent: 'center', marginBottom: 4 }}
              >
                {index > 0 ? (
                  <View
                    style={[
                      { position: 'absolute', left: 0, right: '50%' },
                      lineStyle(isLate && milestone === 'today', isReached),
                    ]}
                  />
                ) : null}
                {nextMilestone ? (
                  <View
                    style={[
                      { position: 'absolute', left: '50%', right: 0 },
                      lineStyle(isLate && milestone === 'end', startOfDay(dates[nextMilestone]) <= startOfDay(now)),
                    ]}
                  />
                ) : null}
                <View
                  style={{
                    alignSelf: 'center',
                    width: isToday ? 24 : 12,
                    height: isToday ? 24 : 12,
                    borderRadius: 12,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isToday
                      ? theme.colors[isLate ? 'surface.brand.secondary-4.subtle' : 'surface.tertiary-neutral']
                      : theme.colors['surface.secondary-neutral'],
                  }}
                >
                  <View
                    style={{
                      width: isToday ? 10 : 8,
                      height: isToday ? 10 : 8,
                      borderRadius: 5,
                      borderWidth: milestone === 'end' ? 2 : 0,
                      borderColor: markerColor,
                      backgroundColor: milestone === 'end' ? theme.colors['surface.secondary-neutral'] : markerColor,
                    }}
                  />
                </View>
              </View>
              <Column alignItems="center" paddingHorizontal="spacing.general.x1" gap="spacing.general.x0,5">
                <Text
                  variant={isToday ? 'bodySmallMedium' : 'bodySmall'}
                  color={isToday ? 'text.primary' : 'text.secondary'}
                  textAlign="center"
                >
                  {labels[milestone]}
                </Text>
                <Text
                  variant={isToday ? 'bodyBaseStrong' : 'bodyBaseMedium'}
                  textAlign="center"
                  style={{ color: isLate && isToday ? overdueColor : theme.colors['text.primary'] }}
                >
                  {localeAwareFormat(dates[milestone], t('dateFormat_MMMd', { ns: 'common' }))}
                </Text>
                {times[milestone] ? (
                  <Text variant="bodySmall" color="text.secondary" textAlign="center">
                    {times[milestone]}
                  </Text>
                ) : null}
              </Column>
            </Column>
          );
        })}
      </Row>
    </Column>
  );
}
